import { useState } from "react";
import axios from "axios";
import { X, Upload, FileText, Trash2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CredentialFormFields } from "@/components/org/credentials/CredentialFormFields";
import { formatFileSize } from "@/lib/credentials";

const EMPTY_FORM = {
  credential_type: "license", credential_name: "", description: "", credential_number: "", issuer: "",
  holder_type: "organization", holder_id: null, issue_date: "", expiration_date: "", does_not_expire: false,
  scope_type: null, scope_reference: null, notes: "",
};

export const CredentialAddModal = ({ meta, currentUserId, onClose, onSaved }) => {
  const { API } = useAuth();
  const canManageOrg = !!meta?.can_manage_organization_credentials;
  const [form, setForm] = useState(
    canManageOrg ? EMPTY_FORM : { ...EMPTY_FORM, holder_type: "person", holder_id: currentUserId || null },
  );
  const [stagedFiles, setStagedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState("");

  const addFiles = (files) => {
    const next = Array.from(files || []).map((file) => ({ file, id: `${file.name}-${file.size}-${Date.now()}` }));
    setStagedFiles((prev) => [...prev, ...next]);
  };

  const removeStaged = (id) => setStagedFiles((prev) => prev.filter((f) => f.id !== id));

  const save = async () => {
    if (!form.credential_name.trim()) {
      setError("Credential name is required.");
      return;
    }
    if (form.holder_type === "person" && !form.holder_id) {
      setError("Please select a person for this credential.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        credential_name: form.credential_name.trim(),
        description: form.description.trim() || null,
        credential_number: form.credential_number.trim() || null,
        issuer: form.issuer.trim() || null,
        issue_date: form.issue_date || null,
        expiration_date: form.does_not_expire ? null : (form.expiration_date || null),
        notes: form.notes.trim() || null,
      };
      const { data } = await axios.post(`${API}/org/credentials`, payload, { withCredentials: true });
      const credentialId = data.credential.credential_id;
      for (let i = 0; i < stagedFiles.length; i++) {
        const fd = new FormData();
        fd.append("file", stagedFiles[i].file);
        setUploadProgress({ index: i, total: stagedFiles.length, percent: 0 });
        await axios.post(`${API}/org/credentials/${credentialId}/documents`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => setUploadProgress({ index: i, total: stagedFiles.length, percent: Math.round((evt.loaded * 100) / (evt.total || 1)) }),
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add this credential.");
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4" data-testid="credential-add-overlay">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl dark:bg-[#0B1220]" data-testid="credential-add-modal">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold text-slate-950 dark:text-white">Add Credential</h2>
          <button type="button" onClick={onClose} data-testid="credential-add-close-btn" className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50 dark:border-white/10 dark:text-slate-400 dark:hover:bg-white/10">
            <X size={16} />
          </button>
        </div>

        <div className="mt-4">
          <CredentialFormFields form={form} setForm={setForm} meta={meta} testIdPrefix="credential-add" />
        </div>

        <div className="mt-5">
          <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Documents</label>
          <label
            htmlFor="credential-add-file-input"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            className="mt-1 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/70 px-4 py-6 text-center dark:border-white/15 dark:bg-white/[0.03]"
            data-testid="credential-add-dropzone"
          >
            <Upload size={20} className="text-slate-400" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Drag &amp; drop or click to browse</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">PDF, JPG, or PNG · up to 10MB each</p>
          </label>
          <input id="credential-add-file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => addFiles(e.target.files)} data-testid="credential-add-file-input" />

          {stagedFiles.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="credential-add-staged-files">
              {stagedFiles.map((f) => (
                <div key={f.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-white/10" data-testid={`credential-add-staged-file-${f.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText size={14} className="shrink-0 text-slate-400" />
                    <span className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{f.file.name}</span>
                    <span className="shrink-0 text-xs text-slate-400">{formatFileSize(f.file.size)}</span>
                  </div>
                  <button type="button" onClick={() => removeStaged(f.id)} data-testid={`credential-add-staged-file-remove-${f.id}`} className="shrink-0 text-slate-400 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {uploadProgress && (
            <div className="mt-3" data-testid="credential-add-upload-progress">
              <div className="flex items-center justify-between text-[11px] font-bold text-slate-500 dark:text-slate-400">
                <span>Uploading file {uploadProgress.index + 1} of {uploadProgress.total}…</span>
                <span>{uploadProgress.percent}%</span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                <div className="h-full rounded-full bg-[#0052FF] transition-[width] duration-150 ease-out" style={{ width: `${uploadProgress.percent}%` }} />
              </div>
            </div>
          )}
        </div>

        {error && <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="credential-add-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="credential-add-cancel-btn" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10">Cancel</button>
          <button type="button" onClick={save} disabled={saving || !form.credential_name.trim()} data-testid="credential-add-save-btn" className="rounded-xl bg-[#0052FF] px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? "Adding…" : "Add Credential"}</button>
        </div>
      </div>
    </div>
  );
};
